import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import WithRoutify from "../../hoc/WithRoutify";
import PatientFormService from "../../services/PatientFormService";
import UnifiedSessionService from "../../services/UnifiedSessionService";
import Buttons from "../../components/UI/Buttons";
import ConsentForm from "./Forms/ConsentForm";
import PatientQuestion from "./Forms/PatientQuestion";
import UploadId from "./Forms/UploadId";
import UploadAdditionalDocument from "./Forms/UploadAdditionalDocument";
import FormCompletion from "./Forms/FormCompletion";
import AuthService from "../../services/AuthService";
import { isObject } from "../../utils/commonUtils";
import ActivityConsent from "./Forms/ActivityConsent";
import PatientChartsService from "../../services/PatientChartsService";
import EmrHistoryQuestions from "./Forms/EmrHistoryQuestions";
import DemographicsVerification from "./Forms/DemographicsVerification";
import { QuestionnaireStep } from "../UnifiedPatientSession/UnifiedStepRouter";
import {
  getKioskUnifiedToken,
  isKioskModeActive,
} from "../../utils/patientFormKiosk";
import {
  isUnifiedReentryMode,
  resolveReentryFirstStep,
} from "../../utils/unifiedReviewMode";
import {
  unifiedManifestRowIsAlwaysReopen,
  patientPayloadShowsStepIncomplete,
  buildReentryUnifiedDisplayManifest,
  peekUnifiedIntakeJustCompleted,
  setUnifiedIntakeJustCompleted,
  clearUnifiedIntakeJustCompleted,
} from "../../utils/unifiedIntakeConstants";

const UNIFIED_Q_KEY = "unified_questionnaire";

/** User chose "Continue Anyway" on /patient-start — only then build the questionnaires+EMR-only queue. */
const UNIFIED_CONTINUE_ANYWAY_FLOW = "unified_continue_anyway_flow";
/** First-time full completion in this tab; suppresses always-reopen queue until thank-you screen clears it. */
const UNIFIED_PACKET_JUST_COMPLETED = "unified_packet_just_completed";

function hasContinueAnywayExplicitOptIn() {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(UNIFIED_CONTINUE_ANYWAY_FLOW) === "1";
  } catch {
    return false;
  }
}

function hasPacketJustCompletedFresh() {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(UNIFIED_PACKET_JUST_COMPLETED) === "1";
  } catch {
    return false;
  }
}

/** Attach original API manifest index for unified step-complete. */
function attachBackendIndicesToManifest(manifestArr) {
  return (manifestArr || []).map((item, backendStepIndex) => ({
    ...item,
    __backendStepIndex: backendStepIndex,
  }));
}

/** After Continue Anyway filtering, indices must be 0..n-1 to match a trimmed server manifest (avoids 422 mismatch). */
function reindexUnifiedManifestSequential(manifestArr) {
  return (manifestArr || []).map((item, i) => ({
    ...item,
    __backendStepIndex: i,
  }));
}

function filterUnifiedManifestContinueAnyway(withBackendIndices) {
  return (withBackendIndices || []).filter((item) => unifiedManifestRowIsAlwaysReopen(item));
}

function filterUnifiedManifestPartialResume(withBackendIndices, patientResponse) {
  return (withBackendIndices || []).filter((item) => {
    if (unifiedManifestRowIsAlwaysReopen(item)) return true;
    if (item?.type === "patient_form" && item.step && item.step !== "emr_history") {
      return patientPayloadShowsStepIncomplete(patientResponse, item.step);
    }
    return false;
  });
}

/** True when each row uses __backendStepIndex 0..length-1 (trimmed Continue Anyway queue, or untouched API order). */
function unifiedManifestUsesCompactSequentialIndices(displayManifest) {
  if (!displayManifest?.length) return true;
  return displayManifest.every((m, i) => Number(m.__backendStepIndex) === i);
}

/** Map server's current_step (backend index) to display index in filtered manifest */
function unifiedMapBackendStepToDisplayIndex(displayManifest, backendStepRaw) {
  const backendStep = Number.isFinite(Number(backendStepRaw)) ? Number(backendStepRaw) : 0;
  if (!displayManifest?.length) return 0;
  const len = displayManifest.length;
  // After completing the last unified step the API advances current_step to total_steps (= len), i.e. past the final index.
  if (unifiedManifestUsesCompactSequentialIndices(displayManifest) && backendStep >= len) {
    return len;
  }
  const exact = displayManifest.findIndex((m) => m.__backendStepIndex === backendStep);
  if (exact >= 0) return exact;
  const forward = displayManifest.findIndex((m) => Number(m.__backendStepIndex) > backendStep);
  if (forward >= 0) return forward;
  return len - 1;
}

function unifiedDisplayStepBackendIndex(meta) {
  const m = meta?.manifest?.[meta?.current_step];
  if (m != null && Number.isFinite(Number(m.__backendStepIndex))) {
    return Number(m.__backendStepIndex);
  }
  return typeof meta?.current_step === "number" ? meta.current_step : Number(meta?.current_step) || 0;
}

const KIOSK_FORM_STEP_PRIORITY = [
  "patient_demographics_verification",
  "new_patient_intake_form",
  "actvity_consents",
  "emr_history",
  "patient_medical_history",
  "medical_history_questionnaire",
  "upload_required_id",
  "upload_additional_documents",
];

/** Map manifest row at display index to PatientForm step key (kiosk never uses form_completion). */
function kioskStepFromManifestRow(manifest, displayIndex) {
  if (!manifest?.length) return { key: null, mappingId: null };
  const len = manifest.length;
  const idx = Math.max(0, Math.min(Number(displayIndex) || 0, len - 1));
  const item = manifest[idx];
  if (item?.type === "questionnaire") {
    return { key: UNIFIED_Q_KEY, mappingId: item.activity_mapping_id };
  }
  if (item?.type === "patient_form" && item.step) {
    return { key: item.step, mappingId: null };
  }
  return { key: null, mappingId: null };
}

function inferCurrentKeyFromPatientPayloadKiosk(response) {
  if (!response) return KIOSK_FORM_STEP_PRIORITY[0];
  if (response.patient_demographics_verification === 1 && !response.demographics_verified_at) {
    return "patient_demographics_verification";
  }
  const formKey = KIOSK_FORM_STEP_PRIORITY.find((key) => {
    if (key === "new_patient_intake_form") {
      return response[key] && response.consent_form;
    }
    return response[key] === 1;
  });
  if (formKey === "new_patient_intake_form") {
    if (response.consent_form) {
      return formKey;
    }
    const nextForm = KIOSK_FORM_STEP_PRIORITY.find((key) => {
      if (key === "new_patient_intake_form") return false;
      return response[key] === 1;
    });
    return nextForm || KIOSK_FORM_STEP_PRIORITY[0];
  }
  return formKey || KIOSK_FORM_STEP_PRIORITY[0];
}

/** Kiosk: always show the live step from session manifest + current_step. Show form_completion when all steps are done. */
function resolveKioskUnifiedStep(response, snap) {
  if (snap?.manifest?.length) {
    const currentStep = snap.current_step ?? 0;
    const manifestLen = snap.manifest.length;
    if (currentStep >= manifestLen || snap.status === "completed") {
      return { key: "form_completion", mappingId: null };
    }
    return kioskStepFromManifestRow(snap.manifest, currentStep);
  }
  if (response?.form_status === "Completed" || response?.form_status === "completed") {
    return { key: "form_completion", mappingId: null };
  }
  return { key: inferCurrentKeyFromPatientPayloadKiosk(response), mappingId: null };
}

const PatientForm = (props) => {
  const [formData, setFormData] = useState({});
  const [patientFormData, setPatientFormData] = useState({});
  const [validation, setValidation] = useState({});
  const [currentKey, setCurrentKey] = useState(null);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isKiosk = isKioskModeActive(location);
  const [consentForm, setConsentForm] = useState(null);
  const patient_form_id = query.get("patient_form_id");
  const [unifiedToken, setUnifiedToken] = useState(
    () => query.get("unified_token") || (isKiosk ? getKioskUnifiedToken() : null)
  );
  const kioskUnifiedSession = isKiosk && Boolean(unifiedToken);
  const reentry_query = query.get("reentry");
  const review_mode_query = query.get("review_mode");
  const thank_you_land = query.get("thank_you_land");

  const isReentryActive = (snap) => {
    if (isKiosk) {
      return false;
    }
    if (isUnifiedReentryMode(reentry_query) || isUnifiedReentryMode(review_mode_query)) {
      return true;
    }
    if (sessionStorage.getItem("unified_reentry") === "1") {
      return true;
    }
    if (isUnifiedReentryMode(snap?.reentry_mode) || isUnifiedReentryMode(snap?.review_mode)) {
      return true;
    }
    return false;
  };
  const [patinetEmrHistory, setPatientEmrHistory] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [unifiedMeta, setUnifiedMeta] = useState(null);
  const [questionnaireManifest, setQuestionnaireManifest] = useState([]);
  const [unifiedQuestionnaireMappingId, setUnifiedQuestionnaireMappingId] = useState(null);
  const unifiedMetaRef = useRef(null);

  useEffect(() => {
    unifiedMetaRef.current = unifiedMeta;
  }, [unifiedMeta]);

  const PatientForms = {
    patient_demographics_verification: DemographicsVerification,
    new_patient_intake_form: ConsentForm,
    actvity_consents: ActivityConsent,
    emr_history: EmrHistoryQuestions,
    patient_medical_history: PatientQuestion,
    medical_history_questionnaire: PatientQuestion,
    upload_required_id: UploadId,
    upload_additional_documents: UploadAdditionalDocument,
    form_completion: FormCompletion,
  };

  const packetTrulySubmitted = (r) =>
    !!(r?.form_status === "Completed" || r?.form_status === "completed");

  const inferCurrentKeyFromPatientPayload = (response) => {
    if (unifiedToken && isReentryActive(null)) {
      const start = resolveReentryFirstStep(response, unifiedMetaRef.current);
      if (start.key === "unified_questionnaire") {
        return UNIFIED_Q_KEY;
      }
      return start.key;
    }
    const formPriority = [
      "patient_demographics_verification",
      "new_patient_intake_form",
      "actvity_consents",
      "emr_history",
      "patient_medical_history",
      "medical_history_questionnaire",
      "upload_required_id",
      "upload_additional_documents",
      "form_completion",
    ];
    if (response.patient_demographics_verification === 1 && !response.demographics_verified_at) {
      return "patient_demographics_verification";
    }
    const formKey = formPriority.find((key) => {
      if (key === "new_patient_intake_form") {
        return response[key] && response.consent_form;
      }
      return response[key] === 1;
    });
    if (formKey === "new_patient_intake_form") {
      if (response.consent_form) {
        return formKey;
      }
      const nextForm = formPriority.find((key) => {
        if (key === "new_patient_intake_form") return false;
        return response[key] === 1;
      });
      return nextForm || "form_completion";
    }
    return formKey || "form_completion";
  };

  const resolveUnifiedStep = (response, snap) => {
    if (isKiosk) {
      return resolveKioskUnifiedStep(response, snap);
    }
    if (unifiedToken && peekUnifiedIntakeJustCompleted() && packetTrulySubmitted(response)) {
      return { key: "form_completion", mappingId: null };
    }
    const reentry = isReentryActive(snap);
    const packetCompleted =
      response?.form_status === "Completed" && (snap?.status === "completed" || snap?.status === "Completed");
    const continueAnywayOnlyReopen =
      reentry &&
      packetCompleted &&
      hasContinueAnywayExplicitOptIn() &&
      !hasPacketJustCompletedFresh();

    if (reentry && !continueAnywayOnlyReopen) {
      const start = resolveReentryFirstStep(response, snap);
      if (start.key === "unified_questionnaire") {
        return { key: UNIFIED_Q_KEY, mappingId: start.mappingId };
      }
      return start;
    }

    if (!snap) {
      return { key: inferCurrentKeyFromPatientPayload(response), mappingId: null };
    }

    const sessionMarkedComplete = snap.status === "completed" || snap.status === "Completed";
    if (sessionMarkedComplete && !continueAnywayOnlyReopen && !reentry && packetTrulySubmitted(response)) {
      return { key: "form_completion", mappingId: null };
    }

    /**
     * After the last questionnaire/EMR step-complete, API sets current_step === total_steps (cursor past final index).
     * unifiedMapBackendStepToDisplayIndex maps that onto the last manifest row → endless last form. Treat as finished.
     */
    if (continueAnywayOnlyReopen && snap.manifest) {
      const total =
        snap.total_steps != null && snap.total_steps !== ""
          ? Number(snap.total_steps)
          : snap.manifest.length || 0;
      const raw = Number(snap.server_backend_step_raw);
      if (Number.isFinite(raw) && total > 0 && raw >= total) {
        return { key: "form_completion", mappingId: null };
      }
    }

    const manifest = snap.manifest || [];
    const idx = snap.current_step ?? 0;

    if (idx >= manifest.length) {
      if (packetTrulySubmitted(response)) {
        return { key: "form_completion", mappingId: null };
      }
      if (unifiedToken && isReentryActive(snap)) {
        const start = resolveReentryFirstStep(response, snap);
        if (start.key === "unified_questionnaire") {
          return { key: UNIFIED_Q_KEY, mappingId: start.mappingId };
        }
        return { key: start.key, mappingId: start.mappingId };
      }
      return { key: inferCurrentKeyFromPatientPayload(response), mappingId: null };
    }

    const item = manifest[idx];
    if (item?.type === "questionnaire") {
      return { key: UNIFIED_Q_KEY, mappingId: item.activity_mapping_id };
    }
    if (item?.type === "patient_form") {
      return { key: item.step, mappingId: null };
    }

    return { key: inferCurrentKeyFromPatientPayload(response), mappingId: null };
  };

  const advanceUnifiedAfterSave = async (stepName) => {
    if (!unifiedToken || !unifiedMetaRef.current) return;
    const meta = unifiedMetaRef.current;
    const backendStepIndex = unifiedDisplayStepBackendIndex(meta);
    props.Loader(true);
    const res = await UnifiedSessionService.unifiedCompleteStep(
      unifiedToken,
      backendStepIndex,
      stepName,
      patient_form_id,
      { reentryMode: isKiosk ? false : isReentryActive(unifiedMetaRef.current) }
    );
    if (res?.code !== 200 || !res?.data || typeof res.data !== "object") {
      props.Loader(false);
      const msg =
        typeof res?.data === "string"
          ? res.data
          : "Unable to sync your visit progress. You can continue; if this repeats, contact your clinic.";
      props.Toast.error(msg);
      await fetchPatientForms(false);
      return;
    }
    if (res.data.is_last_step) {
      if (!isKiosk) {
        try {
          setUnifiedIntakeJustCompleted();
          if (
            typeof sessionStorage !== "undefined" &&
            sessionStorage.getItem(UNIFIED_CONTINUE_ANYWAY_FLOW) !== "1"
          ) {
            sessionStorage.setItem(UNIFIED_PACKET_JUST_COMPLETED, "1");
          }
        } catch {
          /* ignore private mode / quota */
        }
      }
      setUnifiedMeta((m) =>
        m
          ? {
              ...m,
              status: "completed",
              current_step: m.manifest?.length ?? m.current_step,
            }
          : m
      );
      setUnifiedQuestionnaireMappingId(null);
      setCurrentKey("form_completion");
      if (!isKiosk) {
        await fetchPatientForms(false);
      }
      props.Loader(false);
      return;
    }
    const nextOrdinal = res.data.next_step;
    const manifest = meta.manifest;
    let nextDisplayIdx;
    const nextNum = Number(nextOrdinal);
    if (unifiedManifestUsesCompactSequentialIndices(manifest) && Number.isFinite(nextNum)) {
      nextDisplayIdx = Math.min(nextNum, manifest.length);
    } else {
      nextDisplayIdx = unifiedMapBackendStepToDisplayIndex(manifest, nextOrdinal);
    }
    const item = manifest[nextDisplayIdx];
    setUnifiedMeta((m) => (m ? { ...m, current_step: nextDisplayIdx } : m));
    let mappingId = null;
    let nextKey = "form_completion";
    if (item?.type === "questionnaire") {
      mappingId = item.activity_mapping_id;
      nextKey = UNIFIED_Q_KEY;
    } else if (item?.type === "patient_form") {
      nextKey = item.step;
    }
    setUnifiedQuestionnaireMappingId(mappingId);
    setCurrentKey(nextKey);
    setFormData({});
    setValidation({});
    props.Loader(false);
  };

  const fetchPatientForms = async (showLoader = true) => {
    try {
      if (showLoader) props.Loader(true);
      const response = await PatientFormService.getPatientFormsById(patient_form_id);
      if (!unifiedToken && response?.unified_session_token) {
        setUnifiedToken(response.unified_session_token);
        if (showLoader) props.Loader(false);
        return;
      }
      setPatientFormData(response);
      if (isKiosk) {
        try {
          sessionStorage.removeItem(UNIFIED_CONTINUE_ANYWAY_FLOW);
          sessionStorage.removeItem(UNIFIED_PACKET_JUST_COMPLETED);
          clearUnifiedIntakeJustCompleted();
        } catch {
          /* ignore */
        }
      } else {
        try {
          if (
            response?.form_status !== "Completed" &&
            response?.form_status !== "completed" &&
            peekUnifiedIntakeJustCompleted()
          ) {
            clearUnifiedIntakeJustCompleted();
          }
        } catch {
          /* ignore */
        }
      }

      let snapshot = null;
      if (unifiedToken) {
        /** Kiosk UI skips reentry walk, but the API still needs reentry=1 to return step_manifest (see getSessionStatus). */
        const unifiedFetchReentry = isKiosk || isReentryActive(null);
        let sr = await UnifiedSessionService.getSession(unifiedToken, patient_form_id, {
          reentryMode: unifiedFetchReentry,
          kiosk: isKiosk,
        });
        if (sr?.code === 200 && sr.data) {
          const reentryFromSession =
            !isKiosk &&
            (unifiedFetchReentry || isUnifiedReentryMode(sr.data.reentry_mode ?? sr.data.review_mode));
          const unifiedSessionCompleted =
            sr.data.status === "completed" || sr.data.status === "Completed";
          const packetFormCompleted =
            response?.form_status === "Completed" || response?.form_status === "completed";
          const packetCompletedUnified = unifiedSessionCompleted && !!packetFormCompleted;
          const continueAnywayOnly =
            reentryFromSession &&
            packetCompletedUnified &&
            hasContinueAnywayExplicitOptIn() &&
            !hasPacketJustCompletedFresh();

          /** If DB manifest was never trimmed for Continue Anyway, login again so the server resets the queue + current_step. */
          if (continueAnywayOnly) {
            const rawManifest = sr.data.step_manifest || [];
            const reopenOnlyLen = filterUnifiedManifestContinueAnyway(attachBackendIndicesToManifest(rawManifest)).length;
            if (reopenOnlyLen > 0 && reopenOnlyLen !== rawManifest.length) {
              const loginRes = await UnifiedSessionService.login({
                token: unifiedToken,
                continue_after_completion: true,
              });
              if (loginRes?.code === 200 && loginRes.data) {
                const srAgain = await UnifiedSessionService.getSession(unifiedToken, patient_form_id, {
                  reentryMode: unifiedFetchReentry,
                });
                if (srAgain?.code === 200 && srAgain.data) {
                  sr = srAgain;
                }
              }
            }
          }

          const fullWithIdx = attachBackendIndicesToManifest(sr.data.step_manifest || []);
          const scenario2ReentryQueue =
            !isKiosk &&
            reentryFromSession &&
            unifiedSessionCompleted &&
            packetFormCompleted &&
            !hasPacketJustCompletedFresh();
          const partialResume = !continueAnywayOnly && !(unifiedSessionCompleted && packetFormCompleted);
          let displayManifest = fullWithIdx;
          if (continueAnywayOnly) {
            const reopenFiltered = filterUnifiedManifestContinueAnyway(fullWithIdx);
            if (!reopenFiltered.length) {
              displayManifest = fullWithIdx;
            } else {
              displayManifest = reindexUnifiedManifestSequential(reopenFiltered);
            }
          } else if (scenario2ReentryQueue) {
            displayManifest = buildReentryUnifiedDisplayManifest(sr.data.step_manifest || [], response);
            if (!displayManifest.length) {
              displayManifest = fullWithIdx;
            }
          } else if (partialResume) {
            displayManifest = filterUnifiedManifestPartialResume(fullWithIdx, response);
            if (!displayManifest.length) {
              const resendSubset =
                sr.data.manifest_resend === true ||
                query.get("manifest_resend") === "1" ||
                kioskUnifiedSession ||
                (sr.data.status === "in_progress" &&
                  (response?.form_status === "Completed" || response?.form_status === "completed"));
              if (!resendSubset || isKiosk) {
                displayManifest = fullWithIdx;
              }
            }
          }
          const serverBackendStep = sr.data.current_step ?? 0;
          const serverBackendStepRaw = Number.isFinite(Number(sr.data.current_step))
            ? Number(sr.data.current_step)
            : 0;
          const totalStepsFromServer =
            sr.data.total_steps != null && sr.data.total_steps !== ""
              ? Number(sr.data.total_steps)
              : displayManifest.length;
          const displayStep = unifiedMapBackendStepToDisplayIndex(displayManifest, serverBackendStep);
          const snapshotTotalSteps =
            continueAnywayOnly || scenario2ReentryQueue || partialResume
              ? displayManifest.length
              : Number.isFinite(totalStepsFromServer)
                ? totalStepsFromServer
                : displayManifest.length;
          snapshot = {
            manifest: displayManifest,
            current_step: displayStep,
            server_backend_step_raw: serverBackendStepRaw,
            total_steps: snapshotTotalSteps,
            status: sr.data.status,
            last_completed_step: sr.data.last_completed_step ?? null,
            organization_id: sr.data.organization_id,
            reentry_mode: reentryFromSession,
            review_mode: reentryFromSession,
          };
          setUnifiedMeta(snapshot);
          setQuestionnaireManifest(snapshot.manifest.filter((s) => s.type === "questionnaire"));
        } else if (unifiedFetchReentry) {
          const statusData = await UnifiedSessionService.getSessionStatus(unifiedToken, {
            reentryMode: true,
          });
          if (statusData?.step_manifest?.length) {
            const rawSt = Number(statusData.current_step ?? 0);
            const statusCompleted =
              statusData.status === "completed" || statusData.status === "Completed";
            const scen2 =
              !isKiosk &&
              statusCompleted &&
              (response?.form_status === "Completed" || response?.form_status === "completed") &&
              !hasPacketJustCompletedFresh();
            const fullWithIdxF = attachBackendIndicesToManifest(statusData.step_manifest || []);
            let displayMF = fullWithIdxF;
            if (scen2) {
              displayMF = buildReentryUnifiedDisplayManifest(statusData.step_manifest || [], response);
              if (!displayMF.length) displayMF = fullWithIdxF;
            }
            const displayStepF = unifiedMapBackendStepToDisplayIndex(displayMF, statusData.current_step ?? 0);
            const totSt = scen2
              ? displayMF.length
              : statusData.total_steps != null && statusData.total_steps !== ""
                ? Number(statusData.total_steps)
                : statusData.step_manifest.length;
            snapshot = {
              manifest: displayMF,
              current_step: displayStepF,
              server_backend_step_raw: rawSt,
              total_steps: Number.isFinite(totSt) ? totSt : displayMF.length,
              status: statusData.status,
              reentry_mode: !isKiosk,
              review_mode: !isKiosk,
            };
            setUnifiedMeta(snapshot);
            setQuestionnaireManifest(snapshot.manifest.filter((s) => s.type === "questionnaire"));
          } else {
            const cached = sessionStorage.getItem("unified_reentry_manifest");
            if (cached) {
              try {
                const manifest = JSON.parse(cached);
                const fullWithIdxC = attachBackendIndicesToManifest(manifest || []);
                let displayMC = fullWithIdxC;
                if (response?.form_status === "Completed" && !hasPacketJustCompletedFresh()) {
                  displayMC = buildReentryUnifiedDisplayManifest(manifest || [], response);
                  if (!displayMC.length) displayMC = fullWithIdxC;
                }
                const displayStepC = unifiedMapBackendStepToDisplayIndex(displayMC, 0);
                snapshot = {
                  manifest: displayMC,
                  current_step: displayStepC,
                  server_backend_step_raw: 0,
                  total_steps: displayMC.length,
                  status: "completed",
                  reentry_mode: true,
                  review_mode: true,
                };
                setUnifiedMeta(snapshot);
                setQuestionnaireManifest(snapshot.manifest.filter((s) => s.type === "questionnaire"));
              } catch {
                /* ignore bad cache */
              }
            }
          }
        }
        if (!snapshot && !unifiedFetchReentry) {
          props.Toast.error("Unable to load your guided visit. Continuing with standard form order.");
          setUnifiedMeta(null);
          setQuestionnaireManifest([]);
        } else if (!snapshot && isKiosk) {
          setUnifiedMeta(null);
          setQuestionnaireManifest([]);
        }
      }

      const resolved = isKiosk
        ? resolveKioskUnifiedStep(response, snapshot)
        : unifiedToken && (snapshot || isReentryActive(null))
          ? resolveUnifiedStep(response, snapshot)
          : { key: inferCurrentKeyFromPatientPayload(response), mappingId: null };
      const thankYouReturn =
        !isKiosk &&
        thank_you_land === "1" &&
        unifiedToken &&
        packetTrulySubmitted(response);
      if (thankYouReturn) {
        setUnifiedQuestionnaireMappingId(null);
        setCurrentKey("form_completion");
      } else {
        setUnifiedQuestionnaireMappingId(resolved.mappingId);
        setCurrentKey(resolved.key);
      }
      setFormData({});
    } catch (error) {
      console.error("Error fetching patient forms:", error);
    } finally {
      if (showLoader) props.Loader(false);
    }
  };

  useEffect(() => {
    fetchPatientForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_form_id, unifiedToken]);

  const onChangeHandler = (name, value) => {
    let obj = {};
    if (name.startsWith("declination_message_checked_")) {
      const index = name.split("_").pop();
      obj[`authorization_message_checked_${index}`] = false;
      if (!value) {
        obj[`is_other_reason_${index}`] = 0;
        obj[`reasons_for_declination_${index}`] = "";
        obj[`other_reason_${index}`] = "";
      }
    } else if (name.startsWith("authorization_message_checked_")) {
      const index = name.split("_").pop();
      obj[`declination_message_checked_${index}`] = false;
    }
    setFormData({
      ...formData,
      [name]: value,
      ...obj,
    });
    setValidation({
      ...validation,
      [name]: "",
    });
  };

  const bulkOnChangeHandler = (data) => {
    setFormData(data);
  };

  const onSubmitHandler = async () => {
    try {
      if (currentKey === "patient_demographics_verification") {
        await saveDemographicsVerification();
        return false;
      }
      if (currentKey === "patient_medical_history") {
        const customMessages = {
          clp_cdl_applicant_holder: "Please answer for CLP CDL applicant holder",
          usdot_fmcsa_medical_certificate: "Please answer for USDOT-FMCSA",
          driver_signature: "Please provide a driver's signature",
          signatured_date: "Please provide a signature date",
        };
        const requiredFields = Object.keys(customMessages);
        let frontendErrors = {};
        requiredFields.forEach((field) => {
          if (!formData[field] || formData[field] === "") {
            frontendErrors[field] = customMessages[field];
          }
        });
        if (Object.keys(frontendErrors).length > 0) {
          setValidation(frontendErrors);
          props.Toast.error("Please complete the required fields.");
          return;
        }
      }
      if (currentKey === "actvity_consents") {
        await saveActivityConsents();
        return false;
      }
      if (currentKey === "emr_history") {
        await saveEmrHistory();
        return false;
      }
      let payload;
      const headers = {};
      if (["upload_required_id", "upload_additional_documents"].includes(currentKey)) {
        let file = "";
        if (formData.file != null && formData.file != undefined) {
          file = formData.file;
        }
        payload = new FormData();
        payload.append("file", file);
        payload.append("employee_id", patientFormData.employee_id);
        payload.append("document_type", currentKey);
        payload.append("key", currentKey);
        payload.append("patient_form_id", patient_form_id);
        headers["Content-Type"] = "multipart/form-data";
      } else {
        payload = {
          ...formData,
          key: currentKey,
          visit_id: patientFormData.visit_id,
          employee_id: patientFormData.employee_id,
          organization_id: patientFormData.organization_id,
          patient_form_id: patientFormData.patient_form_id,
        };
        if (currentKey === "new_patient_intake_form" && patientFormData.consent_form) {
          payload.title_en = patientFormData.consent_form.title_en;
          payload.consent_form_content_en = patientFormData.consent_form.consent_form_content_en;
          payload.acknowledgement_en = patientFormData.consent_form.acknowledgement_en;
        }
      }
      props.Loader(true);
      const response = await PatientFormService.savePatientForm(payload, headers);
      props.Loader(false);
      let updateValidation = {};
      if (response.status && response.status === "ERROR") {
        if (isObject(response.data)) {
          for (let key in response.data) {
            updateValidation = {
              ...updateValidation,
              [key]: response.data[key].join(","),
            };
          }
        } else {
          props.Toast.error(response.data);
        }
        if (Object.keys(updateValidation).length > 0) {
          setValidation(updateValidation);
          return false;
        }
      }
      if (unifiedToken) await advanceUnifiedAfterSave(currentKey);
      else await fetchPatientForms();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const saveActivityConsents = async () => {
    const activities = Object.values(patientFormData.activity_consents || []);
    let hasError = false;
    let updateValidation = {};
    for (let index = 0; index < activities.length; index++) {
      const activity = activities[index];
      const payload = {
        visit_id: patientFormData.visit_id,
        activity_id: activity.activity_id,
        authorization_message_checked: !!formData[`authorization_message_checked_${index}`],
        declination_message_checked: !!formData[`declination_message_checked_${index}`],
        reasons_for_declination: formData[`reasons_for_declination_${index}`] || "",
        is_other_reason: !!formData[`is_other_reason_${index}`],
        other_reason: formData[`other_reason_${index}`] || "",
        patient_signature: formData.patient_signature,
        consent_date: formData.consent_date,
        patient_id: patientFormData.employee_id,
        company_id: patientFormData.company_id,
        organization_uuid: patientFormData.organization.organization_uuid,
        visit_mapping_id: activity.mapping_id,
      };
      try {
        const response = await PatientFormService.saveActivityConsentForm(activity.mapping_id, payload);
        if (response?.status === "ERROR") {
          hasError = true;
          if (typeof response.data === "object") {
            for (let key in response.data) {
              updateValidation = {
                ...updateValidation,
                [key]: response.data[key].join(","),
              };
            }
          } else {
            props.Toast.error(response.data);
          }
        }
      } catch (err) {
        console.error(`Error saving activity ${index}`, err);
        props.Toast.error("Error saving activity consent");
      }
    }
    if (hasError && Object.keys(updateValidation).length > 0) {
      setValidation(updateValidation);
      return false;
    }
    if (unifiedToken) await advanceUnifiedAfterSave("actvity_consents");
    else await fetchPatientForms();
  };

  const saveDemographicsVerification = async () => {
    if (!formData.demographics_verified || formData.demographics_verified === 0) {
      setValidation({ demographics_verified: "Please verify that the information is accurate" });
      props.Toast.error("Please verify the demographics information");
      return false;
    }
    const requiredFields = {
      first_name: "First Name is required",
      last_name: "Last Name is required",
      gender: "Gender is required",
      dob: "Date of Birth is required",
      phone: "Cell Number is required",
      street_address: "Street Address is required",
      city: "City is required",
      state: "State is required",
      zip_code: "Zipcode is required",
      identification_type: "Identification Type is required",
      identification_number: "Identification Number is required",
    };
    let frontendErrors = {};
    Object.keys(requiredFields).forEach((field) => {
      if (!formData[field] || formData[field] === "") {
        frontendErrors[field] = requiredFields[field];
      }
    });
    if (Object.keys(frontendErrors).length > 0) {
      setValidation(frontendErrors);
      props.Toast.error("Please complete all required fields");
      return false;
    }
    const payload = {
      ...formData,
      demographics_verified: formData.demographics_verified,
      patient_form_id: patient_form_id,
      visit_id: patientFormData.visit_id,
      employee_id: patientFormData.employee_id,
      organization_id: patientFormData.organization_id,
      key: "patient_demographics_verification",
    };
    const headers = {};
    try {
      props.Loader(true);
      const response = await PatientFormService.saveDemographicsVerification(payload, headers);
      props.Loader(false);
      if (response.code === 200 || response.status === "SUCCESS") {
        props.Toast.success("Demographics verified successfully");
        if (unifiedToken) await advanceUnifiedAfterSave("patient_demographics_verification");
        else await fetchPatientForms();
      } else {
        let updateValidation = {};
        if (response.status && response.status === "ERROR") {
          if (isObject(response.data)) {
            for (let key in response.data) {
              updateValidation = {
                ...updateValidation,
                [key]: response.data[key].join(", "),
              };
            }
          } else {
            props.Toast.error(response.data || "Failed to save demographics verification");
          }
          if (Object.keys(updateValidation).length > 0) {
            setValidation(updateValidation);
            return false;
          }
        } else {
          props.Toast.error(response.data || "Failed to save demographics verification");
        }
      }
    } catch (error) {
      console.error("Error saving demographics verification:", error);
      props.Loader(false);
      props.Toast.error("Error saving demographics verification");
    }
  };

  const saveEmrHistory = async () => {
    if (!apiData) return;
    const employee_id = patientFormData.employee_id;
    const allTabs = apiData?.data?.original?.data?.sub_tabs || [];
    const forms_data = allTabs.flatMap((tab) => {
      const screen_type = tab.sub_tab_name;
      return tab.forms.map((form) => {
        return {
          form_id: form.emr_form_id,
          screen_type: screen_type,
          emr_field: form.fields
            .map((field) => {
              const fieldValue = patinetEmrHistory?.[field.id];
              if (
                fieldValue === undefined ||
                fieldValue === null ||
                (typeof fieldValue === "object" && Object.keys(fieldValue).length === 0)
              ) {
                return null;
              }
              return { field_id: field.id, value: fieldValue };
            })
            .filter(Boolean),
        };
      });
    });
    const payload = { employee_id, forms_data };
    try {
      props.Loader(true);
      await PatientFormService.saveEmrHistory(patientFormData.organization_id, patientFormData.visit_id, payload);
      props.Loader(false);
      props.Toast.success("EMR History saved successfully.");
      if (unifiedToken) await advanceUnifiedAfterSave("emr_history");
      else await fetchPatientForms();
    } catch (error) {
      console.error("SAVE FAILED", error);
      props.Toast.error("Failed to save EMR History.");
      props.Loader(false);
    }
  };

  const CurrentComponent = PatientForms[currentKey];
  const showQuestionnaire = currentKey === UNIFIED_Q_KEY && unifiedQuestionnaireMappingId && patientFormData?.visit_id;

  return (
    <>
      <div className="sh_cardBox sh_cardbglight">
        {/* <div className="sh_cardHeader">
          <div className=" d-flex justify-content-between">
            <h6 className="text-lg f-500 mb-0 ml-1">Patient Form</h6>
            {AuthService.getToken() ? (
              <Buttons
                className="badge back_btn"
                label={"Back"}
                iconType={"Back"}
                acl={"any"}
                href="/patient-form-list"
              />
            ) : (
              ""
            )}
          </div>
        </div> */}
        <div className="sh_cardBody p-0">
          {unifiedToken && questionnaireManifest.length > 0 ? (
            <div className="px-3 py-2 small text-muted border-bottom">
              Step{" "}
              {(() => {
                const mlen = unifiedMeta?.manifest?.length ?? 0;
                const raw = (unifiedMeta?.current_step ?? 0) + 1;
                const n = mlen > 0 ? Math.min(raw, mlen) : raw;
                return `${n} of ${mlen || "—"}`;
              })()}
            </div>
          ) : null}
          {showQuestionnaire ? (
            <QuestionnaireStep
              activityMappingId={unifiedQuestionnaireMappingId}
              visitId={patientFormData.visit_id}
              employeeId={patientFormData.employee_id}
              organizationId={patientFormData.organization_id}
              onComplete={() => advanceUnifiedAfterSave("questionnaire")}
              Loader={props.Loader}
              Toast={props.Toast}
              patientFacing
            />
          ) : CurrentComponent ? (
            <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              <CurrentComponent
                consentForm={consentForm}
                patientFormData={patientFormData}
                formData={formData}
                bulkOnChangeHandler={bulkOnChangeHandler}
                onChangeHandler={onChangeHandler}
                validation={validation}
                patinetEmrHistory={patinetEmrHistory}
                setPatientEmrHistory={setPatientEmrHistory}
                apiData={apiData}
                setApiData={setApiData}
                isKiosk={isKiosk}
            />
            </fieldset>
          ) : (
            <p>Loading...</p>
          )}
        </div>
        <div className="sh_cardFooter d-flex justify-content-center">
          {currentKey !== "form_completion" && currentKey !== UNIFIED_Q_KEY && (
            <Buttons
              clickHandler={() => onSubmitHandler()}
              className="btn btn-primary"
              acl={"any"}
              label="Submit"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default WithRoutify(PatientForm);
